// Game type definitions

export const GAME_TYPES = [
  {
    id: 'solo_match_play',
    name: 'Solo Match Play',
    shortName: 'Solo MP',
    description: 'Individual match play. Lowest net score wins the hole.',
    format: 'match_play',
    scoring: 'nassau',
    pressType: 'match_play',
    teamSizes: [1],
    numTeams: [2],
    rules: [
      'Individual game for 2 players',
      'Lowest net score wins each hole',
      '100% course handicap, off-low',
      'No half points, no carry-overs',
    ],
  },
  {
    id: 'net_stroke_play',
    name: 'Net Stroke Play',
    shortName: 'Net Stroke',
    description: 'Individual stroke play. Lowest net aggregate score wins each segment.',
    format: 'stroke_play',
    scoring: 'nassau',
    pressType: 'stroke_play',
    teamSizes: [1],
    numTeams: [2, 3, 4, 5, 6, 7, 8],
    rules: [
      'Individual game for any number of players',
      'Lowest net aggregate score wins',
      '95% of course handicap (WHS Appendix C)',
      'Tiebreaker: scorecard playoff',
    ],
  },
  {
    id: 'top2',
    name: 'Top 2',
    shortName: 'Top 2',
    description: 'Match play. Each hole, both teams sum their 2 lowest net scores. Lower team total wins the hole.',
    format: 'match_play',
    scoring: 'nassau',
    pressType: 'match_play',
    teamSizes: [3, 4],
    numTeams: 2,
    mixedFormats: [{ sizes: [3, 4], numTeams: 2 }],
    rules: [
      'Each hole: sum 2 lowest net scores per team',
      'Lower team total wins the hole',
      '90% of differential, off-low (WHS Appendix C)',
      'No half points, no carry-overs',
    ],
  },
  {
    id: 'duels',
    name: 'Duels',
    shortName: 'Duels',
    description: 'Best ball stroke play. Partners take lowest net score each hole. Lowest aggregate wins.',
    format: 'stroke_play',
    scoring: 'nassau',
    pressType: 'stroke_play',
    teamSizes: [2],
    numTeams: [2, 3, 4],
    rules: [
      'Best ball: lowest net score between partners each hole',
      'Partners may be in different groups',
      '85% of course handicap (WHS Appendix C)',
      'Lowest aggregate per segment wins',
      'Tiebreaker: back 9, last 6, last 3, last hole',
    ],
  },
  // Hero — commented out; untested format, may re-enable later
  // {
  //   id: 'hero',
  //   name: 'Hero',
  //   shortName: 'Hero',
  //   description: '1v2 match play. Solo player vs. best ball of a pair. Rotate solo player each 9.',
  //   format: 'match_play',
  //   scoring: 'per_nine',
  //   pressType: 'match_play',
  //   teamSizes: [3],
  //   numTeams: 2,
  //   rules: [
  //     'Solo player net score vs. lowest net of the pair',
  //     'Rotate solo player each 9 holes',
  //     '90% of differential, off-low (WHS Appendix C)',
  //     '1 point per 9-hole game',
  //     'Solo wins/loses full amount; pair splits',
  //   ],
  // },
  {
    id: 'high_low',
    name: 'High-Low',
    shortName: 'Hi-Lo',
    description: 'Low Ball-High Ball. Compare each team\'s best and worst net scores — 2 points per hole.',
    format: 'match_play',
    scoring: 'nassau',
    pressType: 'match_play',
    teamSizes: [2],
    numTeams: 2,
    rules: [
      'Each hole: compare low balls (1 pt) and high balls (1 pt)',
      'Lower net score wins each comparison',
      'Ties are pushed (no point awarded)',
      '90% of differential, off-low (WHS Appendix C)',
      '2 points per hole, up to 36 over 18',
    ],
  },
  {
    id: 'four_ball',
    name: '4-Ball',
    shortName: '4-Ball',
    description: 'Best ball match play. Each hole, lowest net score on each team counts.',
    format: 'match_play',
    scoring: 'nassau',
    pressType: 'match_play',
    teamSizes: [2],
    numTeams: 2,
    rules: [
      'Each hole: lowest net score per team counts',
      'Lower score wins the hole',
      '90% of differential, off-low (WHS Appendix C)',
      'No half points, no carry-overs',
    ],
  },
  {
    id: 'next_ball',
    name: 'Next Ball',
    shortName: 'Next Ball',
    description: 'Like 4-Ball, but ties broken by comparing second-best scores.',
    format: 'match_play',
    scoring: 'nassau',
    pressType: 'match_play',
    teamSizes: [2],
    numTeams: 2,
    rules: [
      'Same as 4-Ball but with tiebreaker',
      'If best balls tie, compare second-best scores',
      '90% of differential, off-low (WHS Appendix C)',
      'If both tie, hole is pushed',
    ],
  },
  {
    id: 'six_pack',
    name: 'Six Pack',
    shortName: '6-Pack',
    description: 'Stroke play. Each nine, team selects 12 of 18 scores to keep. Lowest total wins.',
    format: 'stroke_play',
    scoring: 'nassau',
    pressType: 'stroke_play',
    teamSizes: [2],
    numTeams: [2, 3, 4],
    rules: [
      '2 players × 9 holes = 18 scores per nine',
      'Team must keep exactly 12, drop 6 per nine',
      '85% of course handicap (WHS Appendix C)',
      'Decide take/drop each hole before next hole',
    ],
  },
  {
    id: 'scramble',
    name: '2v2 Scramble',
    shortName: '2v2 Scramble',
    description: 'Two 2-person teams. One team score per hole. Lowest net aggregate wins.',
    format: 'stroke_play',
    scoring: 'nassau',
    pressType: 'stroke_play',
    teamSizes: [2],
    numTeams: [2],
    isScramble: true,
    rules: [
      'Team picks best shot each time, all play from there',
      'One score per team per hole',
      'USGA scramble handicap: 35% low + 15% high',
      'Lowest net aggregate score wins each segment',
    ],
  },
  {
    id: 'stableford',
    name: 'Stableford',
    shortName: 'Stblfd',
    description: 'Team stroke play using modified Stableford points. Highest team points wins.',
    format: 'stroke_play',
    scoring: 'nassau',
    pressType: 'stroke_play',
    teamSizes: [2, 3, 4],
    numTeams: [2, 3, 4],
    mixedFormats: [{ sizes: [3, 4], numTeams: 2 }],
    rules: [
      'Net score converted to points: bogey=1, par=2, birdie=4, eagle=8',
      'Double bogey or worse = 0 points',
      '85% of course handicap (WHS Appendix C)',
      'Both partners\' points added each hole',
    ],
  },
]

// ============================================
// Press configuration constants
// ============================================

export const PRESS_TRIGGER_MODES = [
  { id: '1_down', label: '1 Down - Manual', description: 'Losing team may press when 1+ holes down' },
  { id: '2_down', label: '2 Down - Manual', description: 'Losing team may press when 2+ holes down' },
  { id: 'none', label: 'None', description: 'Either team may declare a press at any time' },
]

export const STROKE_PLAY_TRIGGER_MODES = [
  { id: '1_down', label: '1 Down - Manual', description: 'May press when trailing by 1+ points' },
  { id: '2_down', label: '2 Down - Manual', description: 'May press when trailing by 2+ points' },
  { id: 'none', label: 'None', description: 'Either team may declare a press at any time' },
]

export const DEFAULT_PRESS_CONFIG = {
  triggerMode: '1_down',
  strokePlayTrigger: null,
  strokePlayThreshold: null,
  lastHolePress: false,
  lastHoleHoles: [],
  pressOnPress: true,
  pressLimit: 'unlimited',
}

/** Return a sensible default press config based on the game's press type */
export function getDefaultPressConfig(gameTypeId) {
  const gt = getGameType(gameTypeId)
  if (!gt) return { ...DEFAULT_PRESS_CONFIG }
  if (gt.pressType === 'stroke_play') {
    return {
      ...DEFAULT_PRESS_CONFIG,
      triggerMode: null,
      strokePlayTrigger: '1_down',
    }
  }
  return { ...DEFAULT_PRESS_CONFIG }
}

export function getGameType(id) {
  return GAME_TYPES.find((g) => g.id === id)
}

/** Returns true if this game type only supports Nassau format (no Standard option) */
export function isNassauOnly(gameTypeId) {
  const gt = getGameType(gameTypeId)
  return gt?.nassauOnly === true
}

/** Return handicap percentage per WHS Appendix C (2024 update) */
export function getHandicapPercent(gameTypeId) {
  switch (gameTypeId) {
    case 'solo_match_play': return 1.0
    case 'net_stroke_play': return 0.95
    case 'top2':
    case 'hero':
    case 'high_low':
    case 'four_ball':
    case 'next_ball': return 0.90
    case 'duels':
    case 'six_pack':
    case 'stableford': return 0.85
    case 'scramble': return null // uses custom weighted team formula
    default: return 0.85
  }
}

export function getValidTeamCount(gameType) {
  if (Array.isArray(gameType.numTeams)) return gameType.numTeams
  return [gameType.numTeams]
}

/** Return format string like "3v3" for a given team size and team count */
function formatLabel(teamSize, numTeams) {
  if (teamSize === 1 && numTeams > 3) return `${numTeams} players`
  return Array(numTeams).fill(teamSize).join('v')
}

/** Get all valid format labels for a game type (e.g. ["3v3", "4v4", "3v4"]) */
export function getFormatLabels(gameType) {
  const teamCounts = Array.isArray(gameType.numTeams) ? gameType.numTeams : [gameType.numTeams]
  const labels = []
  for (const size of gameType.teamSizes) {
    for (const count of teamCounts) {
      labels.push(formatLabel(size, count))
    }
  }
  if (gameType.mixedFormats) {
    for (const mf of gameType.mixedFormats) {
      labels.push(mf.sizes.join('v'))
    }
  }
  return labels
}

/** Get the format label that matches a specific player count, or all labels if no count given */
export function getFormatForPlayerCount(gameType, playerCount) {
  const teamCounts = Array.isArray(gameType.numTeams) ? gameType.numTeams : [gameType.numTeams]
  for (const size of gameType.teamSizes) {
    for (const count of teamCounts) {
      if (size * count === playerCount) return formatLabel(size, count)
    }
  }
  if (gameType.mixedFormats) {
    for (const mf of gameType.mixedFormats) {
      if (mf.sizes.reduce((s, v) => s + v, 0) === playerCount) return mf.sizes.join('v')
    }
  }
  return null
}

/** Return all valid player-per-group counts for a game type */
export function getValidGroupSizes(gameTypeId) {
  const gt = getGameType(gameTypeId)
  if (!gt) return []
  const sizes = new Set()
  const teamCounts = Array.isArray(gt.numTeams) ? gt.numTeams : [gt.numTeams]
  const allowedCounts = gt.format === 'match_play'
    ? teamCounts.filter(c => c <= 2)
    : teamCounts
  for (const size of gt.teamSizes) {
    for (const count of allowedCounts) {
      sizes.add(size * count)
    }
  }
  if (gt.mixedFormats) {
    const allowedMixed = gt.format === 'match_play'
      ? gt.mixedFormats.filter(mf => mf.numTeams <= 2)
      : gt.mixedFormats
    for (const mf of allowedMixed) {
      sizes.add(mf.sizes.reduce((s, v) => s + v, 0))
    }
  }
  return [...sizes].sort((a, b) => a - b)
}

/** Filter game types to only those compatible with a given player count.
 *  Match play games are excluded when they would require more than 2 teams. */
export function getCompatibleGames(playerCount) {
  return GAME_TYPES.filter((gt) => {
    const teamCounts = Array.isArray(gt.numTeams) ? gt.numTeams : [gt.numTeams]
    // For match play, only allow configurations with exactly 2 teams
    const allowedCounts = gt.format === 'match_play'
      ? teamCounts.filter((c) => c <= 2)
      : teamCounts
    const equalMatch = gt.teamSizes.some((size) =>
      allowedCounts.some((count) => size * count === playerCount)
    )
    if (equalMatch) return true
    if (gt.mixedFormats) {
      const allowedMixed = gt.format === 'match_play'
        ? gt.mixedFormats.filter((mf) => mf.numTeams <= 2)
        : gt.mixedFormats
      return allowedMixed.some((mf) => mf.sizes.reduce((s, v) => s + v, 0) === playerCount)
    }
    return false
  })
}

/** Get the handicap percentages for a 3v4 game. Returns null if not 3v4. */
export function get3v4HandicapPercents(gameTypeId, teams) {
  if (!teams || teams.length !== 2) return null
  const sizes = teams.map((t) => t.playerIds.length).sort((a, b) => a - b)
  if (sizes[0] !== 3 || sizes[1] !== 4) return null

  if (gameTypeId === 'top2') {
    return { 3: 0.90, 4: 0.80 }
  }
  if (gameTypeId === 'stableford') {
    return { 3: 0.85, 4: 0.75 }
  }
  return null
}

/** Return human-readable WHS handicap description for a game type */
export function getHandicapDescription(gameTypeId) {
  switch (gameTypeId) {
    case 'solo_match_play':
      return '100% of course handicap differential, off the low player'
    case 'net_stroke_play':
      return '95% of course handicap'
    case 'top2':
    case 'high_low':
    case 'four_ball':
    case 'next_ball':
      return '90% of course handicap differential, off the low player'
    case 'duels':
    case 'six_pack':
      return '85% of course handicap'
    case 'stableford':
      return '85% of course handicap'
    case 'scramble':
      return 'USGA scramble allowances: 35% low + 15% high, off-low between teams'
    default:
      return '85% of course handicap'
  }
}

/** Return true if the game type uses match play format (differential/off-low method) */
export function isMatchPlayGame(gameTypeId) {
  const gt = getGameType(gameTypeId)
  return gt?.format === 'match_play'
}
